import React, { useState, useEffect, useCallback } from 'react';
import {
  CloudIcon,
  ServerIcon,
  EnvelopeIcon,
  DocumentIcon,
  UserIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChartBarIcon,
  CogIcon,
  TrashIcon,
  ArchiveBoxIcon,
  FolderIcon,
  CircleStackIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import {
  CloudIcon as CloudIconSolid
} from '@heroicons/react/24/solid';

import axiosInstance from '../api/axiosInstance';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import { useAdminData } from '../../context/AdminDataContext';
import logger from '../../utils/logger';

const ModernStorage = ({ showNotification }) => {
  const { getStorage, isLoading } = useAdminData();
  const [storageData, setStorageData] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(null);

  const fetchStorageData = useCallback(async (forceRefresh = false) => {
    try {
      let response;

      if (!forceRefresh) {
        try {
          response = await getStorage();
          if (!response?.storage) {
            throw new Error('No cached data');
          }
          response = { data: { success: true, storage: response.storage } };
        } catch (error) {
          response = await axiosInstance.get(
            `/api/business/storage/analytics?timeRange=${timeRange}`
          );
        }
      } else {
        response = await axiosInstance.get(
          `/api/business/storage/analytics?timeRange=${timeRange}`
        );
      }

      if (response.data?.success) {
        // Debug: Log the raw storage data to identify correct quota field

        logger.log('Raw storage data:', JSON.stringify(response.data.storage, null, 2));
        logger.log('Available fields:', Object.keys(response.data.storage || {}));

        setStorageData(response.data.storage);
      } else {
        setStorageData({
          totalStorage: 0,
          usedStorage: 0,
          emailStorage: 0,
          fileStorage: 0,
          otherStorage: 0,
          activeUsers: 0,
          storageGrowth: 0,
          emailGrowth: 0,
          fileGrowth: 0,
          quota: 0,
          usageTrend: [],
          storageDistribution: [],
          topUsers: [],
          alerts: [],
          recentActivity: []
        });
      }
    } catch (error) {
      logger.error('Error fetching storage data:', error);
      showNotification && showNotification('Failed to load storage data', 'error');
    }
  }, [timeRange, showNotification, getStorage]);

  useEffect(() => {
    fetchStorageData();
  }, [fetchStorageData]);

  // Utility functions with proper error handling
  const formatBytes = (bytes) => {
    if (bytes === undefined || bytes === null || !Number.isFinite(bytes) || bytes < 0) {
      return '0 B';
    }
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const safeIndex = Math.max(0, Math.min(i, sizes.length - 1));
    return `${parseFloat((bytes / Math.pow(k, safeIndex)).toFixed(2))} ${sizes[safeIndex]}`;
  };

  const getStoragePercentage = (used, total) => {
    if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) {
      return 0;
    }
    const percentage = (used / total) * 100;
    return Number.isFinite(percentage) ? Math.min(percentage, 100) : 0;
  };

  const handleCleanup = async (type) => {
    try {
      setCleaningUp(type);
      const response = await axiosInstance.post('/api/business/storage/cleanup', { type });

      if (response.data?.success) {
        showNotification && showNotification(`${type} cleanup completed successfully`, 'success');
        fetchStorageData(true);
      }
    } catch (error) {
      showNotification && showNotification('Cleanup failed', 'error');
    } finally {
      setCleaningUp(null);
    }
  };

  const loading = isLoading('storage');

  // Loading state
  if (loading && !storageData) {
    return (
      <div className="h-full bg-gray-50 dark:bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-orange-200 dark:border-orange-900/30 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
          </div>
          <p className="mt-4 text-gray-600 dark:text-gray-400 font-medium">Loading storage data...</p>
        </div>
      </div>
    );
  }

  // Calculate used storage from components if not provided by API
  // For business accounts, usedStorage should be the sum of all storage types
  const emailStorage = Number(storageData?.emailStorage) || 0;
  const fileStorage = Number(storageData?.fileStorage) || 0;
  const otherStorage = Number(storageData?.otherStorage) || 0;
  const calculatedUsedStorage = emailStorage + fileStorage + otherStorage;

  // Use API-provided usedStorage if valid, otherwise use calculated value
  const usedStorage = (storageData?.usedStorage && Number(storageData?.usedStorage) > 0)
    ? Number(storageData?.usedStorage)
    : calculatedUsedStorage;

  // Get total storage quota - check multiple possible field names
  // The quota is the maximum allowed storage (e.g., 500GB), not the used storage
  const getQuotaValue = () => {
    // Check for explicit quota/limit fields first (these should be the allocated limit)
    const quotaFields = [
      storageData?.quota,
      storageData?.storageLimit,
      storageData?.maxStorage,
      storageData?.allocatedStorage,
      storageData?.storageQuota,
      storageData?.totalLimit,
      storageData?.limit
    ];

    for (const field of quotaFields) {
      const value = Number(field);
      if (value && Number.isFinite(value) && value > 0) {
        return value;
      }
    }

    // If totalStorage is significantly larger than usedStorage, it's likely the quota
    const totalStorageValue = Number(storageData?.totalStorage);
    if (totalStorageValue && Number.isFinite(totalStorageValue) && totalStorageValue > usedStorage * 1.1) {
      return totalStorageValue;
    }

    // Fallback: return totalStorage or 0
    return totalStorageValue || 0;
  };

  const totalStorage = getQuotaValue();

  // Two DISTINCT measures — keeping them separate fixes the old conflict where
  // the hero bar looked "full" while the ring read "0% Used":
  //
  //  1) Usage vs quota  → how full the whole allocation is (ring + hero bar).
  //     A 652 MB / 1 TB account is ~0%, so the bar should be nearly empty.
  //  2) Composition of used → each type's share of what's used (breakdown
  //     cards/legend). Files can be 100% of used while usage is still ~0%.
  const usagePercentage = getStoragePercentage(usedStorage, totalStorage);

  // (1) Quota-relative — segments sum to usagePercentage; drives the hero bar.
  const emailOfQuota = getStoragePercentage(emailStorage, totalStorage);
  const fileOfQuota = getStoragePercentage(fileStorage, totalStorage);
  const otherOfQuota = getStoragePercentage(otherStorage, totalStorage);

  // (2) Composition — each type as a share of used storage; drives breakdown.
  const emailPercentage = getStoragePercentage(emailStorage, usedStorage || totalStorage);
  const filePercentage = getStoragePercentage(fileStorage, usedStorage || totalStorage);
  const otherPercentage = getStoragePercentage(otherStorage, usedStorage || totalStorage);

  // Render a hairline-visible segment for any non-zero-but-tiny usage so a
  // 652 MB-on-1 TB account still shows a sliver rather than an empty bar.
  const visibleWidth = (pct, value) => (value > 0 ? Math.max(pct, 0.75) : 0);

  // Storage breakdown data - use the calculated values
  const storageBreakdown = [
    {
      name: 'Email',
      value: emailStorage,
      percentage: emailPercentage,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-100 dark:bg-blue-900/30',
      icon: EnvelopeIcon,
      growth: storageData?.emailGrowth || 0
    },
    {
      name: 'Files',
      value: fileStorage,
      percentage: filePercentage,
      color: 'bg-emerald-500',
      lightColor: 'bg-emerald-100 dark:bg-emerald-900/30',
      icon: FolderIcon,
      growth: storageData?.fileGrowth || 0
    },
    {
      name: 'Other',
      value: otherStorage,
      percentage: otherPercentage,
      color: 'bg-amber-500',
      lightColor: 'bg-amber-100 dark:bg-amber-900/30',
      icon: CircleStackIcon,
      growth: 0
    }
  ];

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800">
        <div className="px-6 pt-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CloudIconSolid className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Storage Management</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCleanupModal(true)}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors"
              >
                <SparklesIcon className="w-3.5 h-3.5" />
                Cleanup
              </button>
              <button
                onClick={() => fetchStorageData(true)}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-md transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-2 p-1 bg-gray-100 dark:bg-[#1a1a1a] rounded-lg w-fit">
            {['overview', 'breakdown', 'users', 'activity'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  activeTab === tab
                    ? 'bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-h-0 overflow-auto p-6 space-y-6">
        {/* Main Storage Card */}
        <div className="bg-white dark:bg-[#111111] rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              {/* Circular Progress */}
              <div className="flex-shrink-0">
                <div className="relative w-40 h-40">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      className="text-gray-200 dark:text-gray-800"
                    />
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      strokeLinecap="round"
                      className={`${
                        usagePercentage >= 90 ? 'text-red-500' :
                        usagePercentage >= 75 ? 'text-amber-500' :
                        'text-orange-500'
                      } transition-all duration-1000`}
                      strokeDasharray={`${usagePercentage * 4.4} 440`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">
                      {usagePercentage > 0 && usagePercentage < 1
                        ? '<1%'
                        : `${usagePercentage.toFixed(0)}%`}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Used</span>
                  </div>
                </div>
              </div>

              {/* Storage Info */}
              <div className="flex-1 space-y-4">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      {formatBytes(usedStorage)}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      of {formatBytes(totalStorage)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {formatBytes(totalStorage - usedStorage)} available
                  </p>
                </div>

                {/* Usage bar — sized against the QUOTA so it agrees with the
                    ring (a near-empty allocation reads as near-empty here). */}
                <div className="space-y-2.5">
                  <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full flex">
                      <div
                        className="bg-blue-500 transition-all duration-700"
                        style={{ width: `${visibleWidth(emailOfQuota, emailStorage)}%` }}
                      />
                      <div
                        className="bg-emerald-500 transition-all duration-700"
                        style={{ width: `${visibleWidth(fileOfQuota, fileStorage)}%` }}
                      />
                      <div
                        className="bg-amber-500 transition-all duration-700"
                        style={{ width: `${visibleWidth(otherOfQuota, otherStorage)}%` }}
                      />
                    </div>
                  </div>
                  {/* Legend now carries the actual numbers so it's informative
                      even when the bar is too small to read. */}
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs">
                    {[
                      { label: 'Email', value: emailStorage, dot: 'bg-blue-500' },
                      { label: 'Files', value: fileStorage, dot: 'bg-emerald-500' },
                      { label: 'Other', value: otherStorage, dot: 'bg-amber-500' },
                    ].map((seg) => (
                      <div key={seg.label} className="flex items-center gap-1.5">
                        <div className={`w-2.5 h-2.5 ${seg.dot} rounded-full`} />
                        <span className="text-gray-600 dark:text-gray-400">{seg.label}</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formatBytes(seg.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="flex items-center gap-6 pt-2">
                  {(() => {
                    const growth = Number(storageData?.storageGrowth) || 0;
                    const up = growth > 0;
                    const down = growth < 0;
                    const tone = up
                      ? { wrap: 'bg-green-100 dark:bg-green-900/30', icon: 'text-green-600 dark:text-green-400' }
                      : down
                        ? { wrap: 'bg-red-100 dark:bg-red-900/30', icon: 'text-red-600 dark:text-red-400' }
                        : { wrap: 'bg-gray-100 dark:bg-gray-800', icon: 'text-gray-500 dark:text-gray-400' };
                    const TrendIcon = down ? ArrowTrendingDownIcon : ArrowTrendingUpIcon;
                    return (
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 ${tone.wrap} rounded-lg flex items-center justify-center`}>
                          <TrendIcon className={`w-4 h-4 ${tone.icon}`} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {growth === 0 ? 'No change' : `${up ? '+' : ''}${growth}%`}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">This month</p>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                      <UserIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {storageData?.activeUsers || 0}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Active users</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Warning if near limit */}
              {usagePercentage >= 75 && (
                <div className={`flex-shrink-0 p-4 rounded-xl ${
                  usagePercentage >= 90
                    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                }`}>
                  <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon className={`w-5 h-5 flex-shrink-0 ${
                      usagePercentage >= 90 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
                    }`} />
                    <div>
                      <p className={`text-sm font-medium ${
                        usagePercentage >= 90 ? 'text-red-900 dark:text-red-200' : 'text-amber-900 dark:text-amber-200'
                      }`}>
                        {usagePercentage >= 90 ? 'Storage Critical' : 'Storage Warning'}
                      </p>
                      <p className={`text-xs mt-1 ${
                        usagePercentage >= 90 ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'
                      }`}>
                        {usagePercentage >= 90
                          ? 'Immediate action required to free up space'
                          : 'Consider cleaning up unused data'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {storageBreakdown.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.name}
                  className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-gray-800 p-5 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-10 h-10 ${item.lightColor} rounded-lg flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${item.color.replace('bg-', 'text-')}`} />
                    </div>
                    {item.growth !== 0 && (
                      <div className={`flex items-center gap-1 text-xs font-medium ${
                        item.growth > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {item.growth > 0 ? (
                          <ArrowTrendingUpIcon className="w-3.5 h-3.5" />
                        ) : (
                          <ArrowTrendingDownIcon className="w-3.5 h-3.5" />
                        )}
                        {Math.abs(item.growth)}%
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{item.name} Storage</h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {formatBytes(item.value)}
                  </p>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                      <span>{item.percentage.toFixed(1)}% of used</span>
                    </div>
                    <div className="h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${item.color} rounded-full transition-all duration-500`}
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'breakdown' && (
          <div className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Storage Breakdown</h3>
            <div className="space-y-4">
              {storageBreakdown.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.name} className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${item.lightColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-6 h-6 ${item.color.replace('bg-', 'text-')}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900 dark:text-white">{item.name}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatBytes(item.value)} ({item.percentage.toFixed(1)}%)
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${item.color} rounded-full transition-all duration-500`}
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Storage Distribution Chart Placeholder */}
            {storageData?.storageDistribution && storageData.storageDistribution.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-800">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Distribution by Category</h4>
                <div className="space-y-3">
                  {storageData.storageDistribution.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{item.name}</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatBytes((item.value || 0) * 1024 * 1024 * 1024)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Storage Users</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Users consuming the most storage</p>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-800">
              {storageData?.topUsers && storageData.topUsers.length > 0 ? (
                storageData.topUsers.map((user, index) => (
                  <div key={user.id || index} className="p-4 hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">{user.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900 dark:text-white">{formatBytes(user.usage || 0)}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {getStoragePercentage(user.usage || 0, usedStorage).toFixed(1)}% of used
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-12 text-center">
                  <UserIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No user data available</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Latest storage operations</p>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-800">
              {storageData?.recentActivity && storageData.recentActivity.length > 0 ? (
                storageData.recentActivity.map((activity, index) => (
                  <div key={index} className="p-4 hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        activity.type === 'upload' ? 'bg-green-100 dark:bg-green-900/30' :
                        activity.type === 'delete' ? 'bg-red-100 dark:bg-red-900/30' :
                        'bg-blue-100 dark:bg-blue-900/30'
                      }`}>
                        {activity.type === 'upload' ? (
                          <ArrowTrendingUpIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                        ) : activity.type === 'delete' ? (
                          <TrashIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                        ) : (
                          <ArchiveBoxIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white">{activity.action}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">by {activity.user}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900 dark:text-white">{activity.size}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{activity.time}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-12 text-center">
                  <ClockIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No recent activity</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Alerts Section */}
        {storageData?.alerts && storageData.alerts.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 p-6">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <h3 className="font-semibold text-amber-900 dark:text-amber-200">Active Alerts</h3>
            </div>
            <div className="space-y-3">
              {storageData.alerts.map((alert, index) => (
                <div
                  key={alert.id || index}
                  className="flex items-center gap-3 p-3 bg-white dark:bg-[#111111] rounded-lg border border-amber-200 dark:border-amber-800"
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    alert.priority === 'high' ? 'bg-red-500' :
                    alert.priority === 'medium' ? 'bg-amber-500' : 'bg-blue-500'
                  }`} />
                  <p className="text-sm text-gray-700 dark:text-gray-300 flex-1">{alert.message}</p>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    alert.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                    alert.priority === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  }`}>
                    {alert.priority}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cleanup Modal */}
      {showCleanupModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#111111] rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                    <SparklesIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Storage Cleanup</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Free up space by removing unused data</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCleanupModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1a1a] transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {[
                {
                  type: 'attachments',
                  title: 'Old Attachments',
                  description: 'Remove email attachments older than 90 days',
                  icon: EnvelopeIcon,
                  color: 'blue'
                },
                {
                  type: 'temp',
                  title: 'Temporary Files',
                  description: 'Delete temporary files and system cache',
                  icon: FolderIcon,
                  color: 'amber'
                },
                {
                  type: 'deleted',
                  title: 'Trash Items',
                  description: 'Permanently remove items from trash',
                  icon: TrashIcon,
                  color: 'red'
                }
              ].map((item) => {
                const Icon = item.icon;
                const isLoading = cleaningUp === item.type;
                return (
                  <div
                    key={item.type}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 bg-${item.color}-100 dark:bg-${item.color}-900/30 rounded-lg flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 text-${item.color}-600 dark:text-${item.color}-400`} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{item.title}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{item.description}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCleanup(item.type)}
                      disabled={isLoading}
                      className="px-4 py-2 text-sm font-medium text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                          Cleaning...
                        </div>
                      ) : (
                        'Clean'
                      )}
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="p-6 bg-gray-50 dark:bg-[#0a0a0a] border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={() => setShowCleanupModal(false)}
                className="w-full py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernStorage;
